import React, { useContext } from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  CORE,
  CORE_READ,
} from '../../Constants/RBAC';
import OrgsMapContext from '../../Context/OrgsMapContext';
import DM1FaultsConstants from '../../Lib/DM1FaultsConstants';
import j1939Dictionary from '../../Static/j1939Dictionary.json';
import ConditionColumn from './ConditionColumn';
import Can from '../../RBAC/Can';

const DM_CONSTANTS = new DM1FaultsConstants();
const ICON_DISPLAY_MAP = new Map([
  ['alerts:positive', 'text-alert-orange'],
  ['alerts:negative', 'text-icon-link-gray'],
  ['dm1:positive', 'text-active-dm'],
  ['dm1:negative', 'text-icon-link-gray'],
]);

function getDm1DisplayName(SPN, FMI) {
  const spnName = j1939Dictionary[SPN] ? j1939Dictionary[SPN].SPN_Name : SPN;
  return `${spnName} - ${DM_CONSTANTS.listFMIname[FMI]}`;
}

function getSourceDisplayName(SourceAddress) {
  return DM_CONSTANTS.listSourceAddress[SourceAddress] || 'Unknown SA';
}

function truncateValue(value) {
  return Number.isInteger(value) ? value : Number(value).toFixed(2);
}

const AlertSignals = ({ AlertData }) => {
  if (!Array.isArray(AlertData.signals)) return;

  return (
    <div className="container-fluid three-quarter-text">
      <div className="row flex-no-wrap mb-4">
        <div className="col">Signal</div>
        <div className="col">Condition</div>
        <div className="col">Value</div>
      </div>
      {AlertData.signals.map(({ name, signalID, type, threshold, value }) => (
        <div className="row flex-nowrap my-2" key={`${signalID}:${value}`}>
          <div className="col">{name}</div>{' '}
          <div className="col">
            <ConditionColumn threshold={threshold} type={type} />
          </div>
          <div className="col text-alert-orange">{truncateValue(value)}</div>
        </div>
      ))}
    </div>
  );
};

AlertSignals.propTypes = {
  AlertData: PropTypes.shape({
    description: oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    signals: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        signalID: PropTypes.string,
        threshold: oneOfType([PropTypes.number, PropTypes.string]),
        type: PropTypes.string,
        value: PropTypes.number,
      })
    ),
  }),
};

const Dm1Details = ({ SPN, FMI, SourceAddress, TriggeredAt, OC }) => (
  <>
    <div className="my-4 nine-tenth-text">
      <div className="text-muted mb-2">Detected At</div>
      <span>{moment(TriggeredAt).format('MMM Do - h:mma')}</span>
    </div>
    <div className="my-4 nine-tenth-text">
      <div className="text-muted mb-2">SPN</div>
      <span>{SPN}</span>
    </div>
    <div className="my-4 nine-tenth-text">
      <div className="text-muted mb-2">FMI</div>
      <span>{FMI}</span>
    </div>
    <div className="my-4 nine-tenth-text">
      <div className="text-muted mb-2">Source</div>
      <span>{getSourceDisplayName(SourceAddress)}</span>
    </div>
    <div className="my-4 nine-tenth-text">
      <div className="text-muted mb-2">Occurrences</div>
      <span>{OC}</span>
    </div>
  </>
);

Dm1Details.propTypes = {
  FMI: PropTypes.number,
  SPN: PropTypes.number,
  OC: PropTypes.number,
  SourceAddress: PropTypes.number,
  TriggeredAt: PropTypes.string,
};

export default function MobileDetailsModal({
  isOpen,
  toggleModal,
  alert,
  tab,
  handleRuleLinkClick,
  assetView,
}) {
  const orgsMap = useContext(OrgsMapContext);

  const isRuleAlert = tab === 'alerts';
  const {
    Transition,
    GatewayId,
    GatewayName,
    SPN,
    FMI,
    OrgId,
    RuleId,
    RuleName,
    AlertData,
  } = alert;

  const TEXT_COLOR = ICON_DISPLAY_MAP.get(`${tab}:${Transition}`);

  return (
    <Modal
      isOpen={isOpen}
      className="m-0 h-100 mh-100"
      contentClassName="h-100 mh-100 w-100 border-0 rounded-0"
      scrollable
      toggle={() => toggleModal(!isOpen)}
    >
      <ModalHeader
        className="border-0 pb-0"
        toggle={() => toggleModal(!isOpen)}
      />
      <ModalBody className="pt-0">
        <h5 className={`mt-3 mb-4 ${TEXT_COLOR}`}>{`${
          isRuleAlert ? 'Alert' : 'DM1 Fault'
        } Details`}</h5>
        <div className="my-4 nine-tenth-text">
          <div className="text-muted mb-2">Name</div>
          {isRuleAlert ? (
            <Can
              no={() => (
                <button
                  className="btn btn-link p-0 text-decoration-none"
                  disabled
                  onClick={() => null}
                >
                  <span className={TEXT_COLOR}>{RuleName}</span>
                </button>
              )}
              orgID={OrgId}
              perform={ASSET_MODEL_READ}
              resource={ASSET_MODEL}
              yes={() => (
                <button
                  onClick={() => handleRuleLinkClick(GatewayId, RuleId)}
                  className="btn btn-link p-0 text-decoration-none"
                >
                  <span className={TEXT_COLOR}>{RuleName}</span>
                </button>
              )}
            />
          ) : (
            getDm1DisplayName(SPN, FMI)
          )}
        </div>
        {assetView ? null : (
          <div className="my-4 nine-tenth-text">
            <div className="text-muted mb-2">Asset</div>
            <Can
              no={() => (
                <button className="btn btn-link p-0" disabled>
                  {GatewayName}
                </button>
              )}
              orgID={OrgId}
              perform={CORE_READ}
              resource={CORE}
              yes={() => (
                <Link
                  to={{
                    pathname: `/asset/${GatewayId}`,
                    search: '?tab=dashboard',
                  }}
                  replace={true}
                >
                  {GatewayName}
                </Link>
              )}
            />
          </div>
        )}
        <div className="my-4 nine-tenth-text">
          <p className="text-muted mb-2">Organization</p>
          <span className="text-dark">{orgsMap?.[OrgId]?.name}</span>
        </div>
        <div className="my-4 nine-tenth-text">
          <div className="text-muted mb-2">Status</div>
          <span className={TEXT_COLOR}>
            {Transition === 'positive' ? 'Active' : 'Inactive'}
          </span>
        </div>
        <hr className="my-4" />
        {isRuleAlert ? (
          <AlertSignals AlertData={AlertData} />
        ) : (
          <Dm1Details {...alert} />
        )}
      </ModalBody>
    </Modal>
  );
}

MobileDetailsModal.propTypes = {
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  tab: PropTypes.oneOf(['alerts', 'dm1']),
  alert: PropTypes.shape({
    AlertData: PropTypes.shape({
      description: oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      signals: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          signalID: PropTypes.string,
          threshold: oneOfType([PropTypes.number, PropTypes.string]),
          type: PropTypes.string,
          value: PropTypes.number,
        })
      ),
    }),
    Canbus: PropTypes.number,
    CreatedAt: PropTypes.string,
    DetectedAt: PropTypes.string,
    FMI: PropTypes.number,
    GatewayId: PropTypes.string,
    GatewayName: PropTypes.string,
    GpsData: PropTypes.shape({
      Lat: PropTypes.number,
      Long: PropTypes.number,
    }),
    Id: PropTypes.string,
    OC: PropTypes.number,
    OrgId: PropTypes.string,
    RuleId: PropTypes.string,
    RuleName: PropTypes.string,
    Severity: PropTypes.string,
    SourceAddress: PropTypes.number,
    SPN: PropTypes.number,
    Transition: PropTypes.oneOf(['positive', 'negative']),
    TriggeredAt: PropTypes.string,
    Type: PropTypes.oneOf(['rule']),
    UpdatedAt: PropTypes.string,
  }),
  handleRuleLinkClick: PropTypes.func,
  assetView: PropTypes.bool,
};

MobileDetailsModal.defaultProps = { alert: {} };
